import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import FilterDimension from "../FilterDimension";
import { getAllChargingSites, getAllChargingSitesForSessions } from "../../api/ChargingSites";
import { useAuth } from "../../providers/AuthContext";
import {
    clampYearMonth,
    compareYearMonth,
    expandYearMonths,
    getLatestAllowedYearMonth,
    validatePastYearMonthRange,
} from "./gridCostUtils";

const COOKIE_KEY = "gridCalculationsFilters_v1";
const MAX_SELECTED_SITES = 3;

function setCookie(name, value, days = 30) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
    const key = `${encodeURIComponent(name)}=`;
    const parts = document.cookie.split("; ");
    for (const part of parts) {
        if (part.startsWith(key)) {
            return decodeURIComponent(part.slice(key.length));
        }
    }
    return null;
}

function initialMonth(value, maxYearMonth) {
    return clampYearMonth(value || maxYearMonth, maxYearMonth);
}

export default function GridCostCalculationFilters({
    onFiltersChange,
    onLoadingChange,
    onCalculate,
    calculating = false,
    loadingSites = false,
}) {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const maxSelectableMonth = useMemo(() => getLatestAllowedYearMonth(), []);

    const saved = useMemo(() => {
        try {
            const raw = getCookie(COOKIE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    const [sites, setSites] = useState([]);
    const [fromMonth, setFromMonth] = useState(() =>
        initialMonth(saved?.fromMonth, getLatestAllowedYearMonth())
    );
    const [toMonth, setToMonth] = useState(() =>
        initialMonth(saved?.toMonth, getLatestAllowedYearMonth())
    );

    const rawDimension = useMemo(
        () => [
            {
                label: "Charging site",
                options: sites.map((s) => s.name || s.chargingSitePublicId),
                selected: (saved?.selectedSiteNames || []).slice(0, MAX_SELECTED_SITES),
                multiSelect: true,
                searchable: true,
                isActive: false,
                maxSelectedOptions: MAX_SELECTED_SITES,
                maxSelectedOptionsMessage: t("gridCost.maxSites", { count: MAX_SELECTED_SITES }),
            },
        ],
        [sites, saved?.selectedSiteNames, t]
    );

    const [dimension, setDimension] = useState(rawDimension);

    useEffect(() => {
        setDimension((prev) => {
            const prevSelected = prev[0]?.selected || [];
            return [
                {
                    ...rawDimension[0],
                    selected: (prevSelected.length ? prevSelected : rawDimension[0].selected).slice(
                        0,
                        MAX_SELECTED_SITES
                    ),
                },
            ];
        });
    }, [rawDimension]);

    useEffect(() => {
        let cancelled = false;

        const loadSites = async () => {
            try {
                onLoadingChange?.(true);
                const token = await getAccessToken();
                let siteList;
                try {
                    siteList = await getAllChargingSitesForSessions(token);
                } catch {
                    siteList = await getAllChargingSites(token);
                }
                if (!cancelled) {
                    setSites(
                        (siteList || []).slice().sort((a, b) =>
                            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
                        )
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message);
                }
            } finally {
                if (!cancelled) {
                    onLoadingChange?.(false);
                }
            }
        };

        loadSites();

        return () => {
            cancelled = true;
        };
    }, [getAccessToken, onLoadingChange]);

    const selectedSiteNames = (dimension[0]?.selected || []).slice(0, MAX_SELECTED_SITES);
    const sitePublicIds = useMemo(
        () =>
            selectedSiteNames
                .map(
                    (name) =>
                        sites.find((s) => (s.name || s.chargingSitePublicId) === name)?.chargingSitePublicId
                )
                .filter(Boolean),
        [selectedSiteNames, sites]
    );

    const yearMonths = useMemo(
        () => expandYearMonths(fromMonth, toMonth),
        [fromMonth, toMonth]
    );

    const monthRangeError = useMemo(
        () => validatePastYearMonthRange(fromMonth, toMonth, maxSelectableMonth),
        [fromMonth, toMonth, maxSelectableMonth]
    );
    const siteLimitError =
        (dimension[0]?.selected || []).length > MAX_SELECTED_SITES
            ? t("gridCost.selectNoMoreSites", { count: MAX_SELECTED_SITES })
            : null;

    const isValid = useMemo(
        () => Boolean(sitePublicIds.length > 0 && !monthRangeError && !siteLimitError),
        [sitePublicIds, monthRangeError, siteLimitError]
    );

    useEffect(() => {
        onFiltersChange?.({
            sitePublicIds,
            siteNames: selectedSiteNames,
            yearMonths,
            fromMonth,
            toMonth,
            isValid,
            monthRangeError,
            siteLimitError,
        });

        setCookie(
            COOKIE_KEY,
            JSON.stringify({
                selectedSiteNames,
                fromMonth,
                toMonth,
            })
        );
    }, [
        sitePublicIds,
        selectedSiteNames,
        yearMonths,
        fromMonth,
        toMonth,
        isValid,
        monthRangeError,
        siteLimitError,
        onFiltersChange,
    ]);

    const handleFromMonthChange = (value) => {
        const nextFrom = clampYearMonth(value, maxSelectableMonth);
        setFromMonth(nextFrom);
        if (compareYearMonth(nextFrom, toMonth) > 0) {
            setToMonth(nextFrom);
        }
    };

    const handleToMonthChange = (value) => {
        const nextTo = clampYearMonth(value, maxSelectableMonth);
        setToMonth(nextTo);
        if (compareYearMonth(fromMonth, nextTo) > 0) {
            setFromMonth(nextTo);
        }
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg">
            <FilterDimension
                dimension={dimension}
                setdimension={setDimension}
                index={0}
                allowDeleteDimension={false}
            />

            <div className="mt-4 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-mute-600">{t("gridCost.fromMonth")}</span>
                    <input
                        type="month"
                        className="border border-mute-300 rounded px-2 py-1.5 text-sm"
                        value={fromMonth}
                        max={maxSelectableMonth}
                        onChange={(e) => handleFromMonthChange(e.target.value)}
                    />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-mute-600">{t("gridCost.toMonth")}</span>
                    <input
                        type="month"
                        className="border border-mute-300 rounded px-2 py-1.5 text-sm"
                        value={toMonth}
                        max={maxSelectableMonth}
                        onChange={(e) => handleToMonthChange(e.target.value)}
                    />
                </label>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={calculating || loadingSites || !isValid}
                    onClick={onCalculate}
                >
                    {calculating ? t("common.calculating") : t("common.calculate")}
                </button>
            </div>
            <p className="text-xs text-mute-500 mt-3">
                {t("gridCost.onlyCompletedMonths", { month: maxSelectableMonth })}
            </p>
            {monthRangeError ? (
                <p className="text-xs text-red-600 mt-1">{monthRangeError}</p>
            ) : null}
            {siteLimitError ? (
                <p className="text-xs text-red-600 mt-1">{siteLimitError}</p>
            ) : null}
        </div>
    );
}
